import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Plus, Trash2, Ticket } from "lucide-react";
import { Toggle } from "@/pages/admin/AdminProducts";

const CATEGORIES = [
  { value: "", label: "Todas las categorías" },
  { value: "cortes", label: "Cortes" },
  { value: "paquetes", label: "Paquetes" },
  { value: "charcuteria", label: "Charcutería" },
  { value: "quesos", label: "Quesos" },
  { value: "complementos", label: "Complementos" },
  { value: "condimentos", label: "Condimentos" },
  { value: "extras", label: "Extras" },
];

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "", discount_type: "percent", discount_value: 10,
    min_purchase: 0, category: "", max_uses: "", expires_at: "", active: true,
  });
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/coupons").then((r) => {
      setCoupons(r.data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        code: form.code.toUpperCase(),
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        min_purchase: parseFloat(form.min_purchase) || 0,
        category: form.category || null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
      };
      await api.post("/admin/coupons", payload);
      setShowForm(false);
      setForm({ code: "", discount_type: "percent", discount_value: 10, min_purchase: 0, category: "", max_uses: "", expires_at: "", active: true });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    }
  };

  const remove = async (code) => {
    if (!confirm(`¿Eliminar cupón ${code}?`)) return;
    await api.delete(`/admin/coupons/${code}`);
    load();
  };

  const toggleActive = async (c) => {
    await api.patch(`/admin/coupons/${c.code}`, { ...c, active: !c.active });
    load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-coupons">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-2">⟡ Admin</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase text-white leading-none">Cupones</h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-sm tracking-widest uppercase px-5 py-3 inline-flex items-center gap-2"
          data-testid="new-coupon-btn"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Cancelar" : "Nuevo cupón"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-arre-surface border border-arre-border p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="coupon-form">
          <Field label="Código (único)">
            <input
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="COMPARE10"
              className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white font-display tracking-wider focus:outline-none focus:border-arre-red"
              data-testid="coupon-code-input"
            />
          </Field>
          <Field label="Tipo">
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white focus:outline-none focus:border-arre-red">
              <option value="percent">% porcentaje</option>
              <option value="fixed">$ fijo</option>
            </select>
          </Field>
          <Field label={`Valor (${form.discount_type === "percent" ? "%" : "$ MXN"})`}>
            <input
              required type="number" min="1" step="0.01"
              value={form.discount_value}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white focus:outline-none focus:border-arre-red"
            />
          </Field>
          <Field label="Mínimo de compra ($ MXN)">
            <input type="number" min="0" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white focus:outline-none focus:border-arre-red" />
          </Field>
          <Field label="Categoría aplicable">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white focus:outline-none focus:border-arre-red">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Máximo de usos">
            <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Ilimitado" className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white focus:outline-none focus:border-arre-red" />
          </Field>
          <Field label="Expira (opcional)">
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full bg-arre-black border border-arre-border px-3 py-2 text-white focus:outline-none focus:border-arre-red" />
          </Field>
          <Field label="Activo">
            <Toggle active={form.active} onChange={(v) => setForm({ ...form, active: v })} />
          </Field>
          <div className="flex items-end">
            <button type="submit" className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-sm tracking-widest uppercase px-6 py-2" data-testid="save-coupon-btn">
              Crear cupón
            </button>
          </div>
          {err && <div className="col-span-3 text-arre-red text-sm">{err}</div>}
        </form>
      )}

      <div className="bg-arre-surface border border-arre-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-arre-border text-[10px] tracking-[0.2em] uppercase text-white/50">
              <th className="text-left p-4">Código</th>
              <th className="text-left p-4">Descuento</th>
              <th className="text-left p-4">Mínimo</th>
              <th className="text-left p-4">Categoría</th>
              <th className="text-left p-4">Usos</th>
              <th className="text-left p-4">Expira</th>
              <th className="text-left p-4">Activo</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="p-8 text-center text-white/50">Cargando...</td></tr>}
            {!loading && coupons.length === 0 && (
              <tr><td colSpan="8" className="p-10 text-center">
                <Ticket className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <div className="text-white/50">Sin cupones todavía. Crea el primero, compare.</div>
              </td></tr>
            )}
            {coupons.map((c) => (
              <tr key={c.code} className="border-b border-arre-border/50" data-testid={`coupon-${c.code}`}>
                <td className="p-4 font-display text-lg text-white tracking-wider">{c.code}</td>
                <td className="p-4 text-arre-red font-bold">
                  {c.discount_type === "percent" ? `${c.discount_value}%` : fmtMXN(c.discount_value)}
                </td>
                <td className="p-4 text-white/70">{c.min_purchase ? fmtMXN(c.min_purchase) : "—"}</td>
                <td className="p-4 text-white/70 capitalize">{c.category || "Todas"}</td>
                <td className="p-4 text-white/70">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="p-4 text-white/70">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("es-MX") : "—"}</td>
                <td className="p-4">
                  <Toggle active={c.active} onChange={() => toggleActive(c)} />
                </td>
                <td className="p-4">
                  <button onClick={() => remove(c.code)} className="text-white/40 hover:text-arre-red" data-testid={`delete-coupon-${c.code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.25em] uppercase text-white/60 mb-1">{label}</div>
      {children}
    </label>
  );
}
